import { Entity } from "@common/types/entity";
import {
    FormRuleActionType,
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
} from "./constants";

function getEntityIdsFromCondition(
    condition: TransactionRuleConditionFormSchema,
): number[] | undefined {
    if (!condition) {
        return undefined;
    }
    const value = condition.entityValue;
    if (
        condition.conditionType !== FormRuleConditionType.ENTITY ||
        !Array.isArray(value)
    ) {
        return undefined;
    }
    return value;
}

function filterEntitiesByIds({
    entities,
    entityIds,
}: {
    entities: Entity[];
    entityIds: number[];
}): Entity[] {
    if (entityIds.length === 0) {
        return entities;
    }
    return entities.filter((entity) => entityIds.includes(entity.id));
}

export function getEffectiveEntityIds({
    conditions,
    scopedEntityIds,
}: {
    conditions: TransactionRuleConditionFormSchema[];
    scopedEntityIds?: number[];
}): number[] {
    const conditionEntityIds = conditions
        .map(getEntityIdsFromCondition)
        .find((entityIds) => entityIds !== undefined);
    if (conditionEntityIds !== undefined) {
        return conditionEntityIds;
    }
    return scopedEntityIds && scopedEntityIds.length > 0 ? scopedEntityIds : [];
}

export function getEntityActionEntityId(
    actions: TransactionRuleActionFormSchema[],
): number | undefined {
    const entityId = [...actions]
        .reverse()
        .find((action) => action.actionType === FormRuleActionType.ENTITY)
        ?.value?.entityId;

    if (entityId == null) {
        return undefined;
    }

    return entityId;
}

/**
 * A "Move to entity" action re-homes matched transactions, so ledger/account
 * actions must target that entity rather than the condition entities.
 */
export function getAccountTargetEntityIds({
    conditions,
    actions,
    scopedEntityIds,
}: {
    conditions: TransactionRuleConditionFormSchema[];
    actions: TransactionRuleActionFormSchema[];
    scopedEntityIds?: number[];
}): number[] {
    const entityActionEntityId = getEntityActionEntityId(actions);
    if (entityActionEntityId !== undefined) {
        return [entityActionEntityId];
    }
    return getEffectiveEntityIds({ conditions, scopedEntityIds });
}

export function resolveEntitiesForConditionScope({
    entities,
    conditions,
    scopedEntityIds,
}: {
    entities: Entity[];
    conditions: TransactionRuleConditionFormSchema[];
    scopedEntityIds?: number[];
}): Entity[] {
    return filterEntitiesByIds({
        entities,
        entityIds: getEffectiveEntityIds({
            conditions,
            scopedEntityIds,
        }),
    });
}

export function resolveAccountTargetEntities({
    entities,
    conditions,
    actions,
    scopedEntityIds,
}: {
    entities: Entity[];
    conditions: TransactionRuleConditionFormSchema[];
    actions: TransactionRuleActionFormSchema[];
    scopedEntityIds?: number[];
}): Entity[] {
    return filterEntitiesByIds({
        entities,
        entityIds: getAccountTargetEntityIds({
            conditions,
            actions,
            scopedEntityIds,
        }),
    });
}
